import { useEffect, useState } from 'react'
import isEmpty from 'lodash/isEmpty'
import { COOKIE_HUBSPOT } from '@utils/constants'
import Cookie from 'js-cookie'
import { IRadioTileOption } from '@noissue-ui-kit/oldBranding/radioTile'
import {
  TFieldOption,
  TFieldSpecification,
  THubspotFormSpecification,
} from '@utils/form/hubspot/schema'
import { dataLayerPushWithTimestamp } from '@utils/gtm'
import { formatFieldName } from './dependentFields.utils'
import { IHubspotQuotePayload } from './plusForm.types'

export const QUANTITY_OPTION_DIVIDER = '|'

export function usePlusFormFields({
  formSpecification,
}: {
  formSpecification: THubspotFormSpecification
}) {
  const [parsedFields, setParsedFields] = useState<TFieldSpecification[]>(null)

  useEffect(() => {
    if (isEmpty(formSpecification?.fieldGroups)) {
      setParsedFields([])
      return
    }

    const fieldsPerGroup = formSpecification?.fieldGroups
      ?.map((group) => {
        const fieldsToParse = group?.fields

        return fieldsToParse
          ?.map((field) => {
            if (isEmpty(field)) {
              return null
            }

            return {
              fieldType: field?.fieldType,
              description: field?.description,
              label: field?.label,
              name: field?.name,
              placeholder: field?.placeholder,
              hidden: field?.hidden,
              defaultValue: field?.defaultValue,
              dependentFields: field?.dependentFields,

              options: field?.options?.map((option) => {
                return {
                  value: option.value,
                  label: option.label,
                }
              }),
            }
          })
          .filter(Boolean)
      })
      .filter(Boolean)

    setParsedFields([].concat(...fieldsPerGroup))
  }, [formSpecification])

  return parsedFields
}

export const getInitialSelection = (
  formFields: TFieldSpecification[]
): Record<string, string> => {
  const initialSelection: Record<string, string> = {}

  if (isEmpty(formFields)) {
    return null
  }

  const visibleFields = formFields?.filter((x) => !x.hidden)

  for (const field of visibleFields) {
    if (field?.fieldType === 'radio' || field?.fieldType === 'dropdown') {
      initialSelection[formatFieldName(field.name)] = field?.options[0]?.value
    } else {
      initialSelection[formatFieldName(field.name)] = ''
    }
  }

  return initialSelection
}

const isExtraDetailsField = (name) => {
  return (
    name.includes('anything_else_we_should_know') ||
    name.includes('extra_info') ||
    name.includes('extra_details')
  )
}

export const answersToHubspotPayload = ({
  plusFormAnswers,
  productSlug,
  formFields,
  productName,
  productImage,
  sku,
}: {
  plusFormAnswers: Record<string, string>
  productSlug: string
  formFields: TFieldSpecification[]
  productName: string
  productImage: string
  sku?: string
}): IHubspotQuotePayload => {
  // Extract Extra Information key & value pair to send it separate from lineItems.
  const extraInfoEntry = Object.entries(plusFormAnswers).find((entry) =>
    isExtraDetailsField(entry[0])
  )

  // Generate payload fields from user input [plusFormAnswers]
  const visibleFieldsValues = Object.entries(plusFormAnswers)
    .map((entry) => {
      if (!isExtraDetailsField(entry)) {
        const fieldLabel = formFields?.find((x) => x?.name === entry[0])?.label

        return {
          name: entry[0],
          value: entry[1],
          label: fieldLabel,
        }
      }
      return null
    })
    .filter(Boolean)

  // Attach hidden fileds that were sent from hubspot as configuration and wasn't shown to user.
  const hiddenFieldsValues = formFields
    ?.filter((field) => field?.hidden)
    ?.map((field) => {
      return {
        name: field?.name,
        value: field?.defaultValue,
        label: field?.label,
      }
    })
    .filter((x) => Boolean(x.value))

  // Fallback to contentful SKU field if hubspot sku field is missing
  const huspotSKU = hiddenFieldsValues?.find((x) =>
    x?.name?.toLowerCase()?.includes('sku')
  )

  if (!huspotSKU) {
    hiddenFieldsValues.push({
      name: 'sku',
      value: sku,
      label: 'Product SKU from Contentful',
    })
  }

  return {
    fields: [
      {
        name: 'line_items',
        label: 'Line Items',
        value: [
          {
            fields: [
              ...visibleFieldsValues,
              ...hiddenFieldsValues,
              {
                name: 'product_name',
                value: productName,
                label: 'Product Name',
              },
              {
                name: 'product_slug',
                value: productSlug,
                label: 'Product Slug',
              },
              {
                name: 'image_url',
                value: productImage?.startsWith('//images.ctfassets.net/')
                  ? `https:${productImage}`
                  : productImage,
                label: 'Image URL',
              },
              {
                name: 'page_url',
                value: `${window?.location?.origin}${window?.location?.pathname}`,
                label: 'Page URL',
              },
            ],
          },
        ],
      },
      {
        name: extraInfoEntry?.[0] || '',
        value: extraInfoEntry?.[1] || '',
        label: 'Extra Info',
      },
    ],
    context: {
      hutk: Cookie.get(COOKIE_HUBSPOT),
    },
  }
}

export const formSpecOptionToRadioOption = ({
  formOption,
}: {
  formOption: TFieldOption
}): IRadioTileOption => {
  return {
    displayLabel: formOption?.label,
    key: formOption?.value,
    disabled: false,
  }
}

export const getSelectedOption = ({
  plusFormAnswers,
  field,
}: {
  plusFormAnswers: Record<string, string>
  field: TFieldSpecification
}) => {
  const fieldName = formatFieldName(field?.name)

  let selectedValue = field?.options?.find(
    (option) => option.value === plusFormAnswers[fieldName]
  )
  // Set the first option as selected only if there isn't any custom value submitted
  if (!selectedValue && !plusFormAnswers[fieldName]) {
    selectedValue = field?.options?.[0]
  }

  if (!isEmpty(selectedValue)) {
    return formSpecOptionToRadioOption({ formOption: selectedValue })
  }
  return null
}

export const trackRequesAQuotetFormInteraction = ({
  componentName,
  componentValue,
  componentLabel,
  productSlug,
  componentType,
}: {
  componentName: string
  componentValue: string
  componentLabel: string
  productSlug: string
  componentType: string
}) => {
  dataLayerPushWithTimestamp({
    event: 'request-a-quote-form-interaction',
    componentName,
    componentInformation: componentValue,
    componentLabel,
    selectedProductSlug: productSlug,
    displayType: componentType,
  })
}

export const trackRequestAQuoteModalInteraction = ({
  componentValue,
  productSlug,
}: {
  componentValue: string
  productSlug: string
}) => {
  trackRequesAQuotetFormInteraction({
    componentName: 'Quote request success dialog',
    componentValue: componentValue,
    componentType: 'modal',
    componentLabel: 'Quote requested!',
    productSlug: productSlug,
  })
}
